import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { shouldRenderTileIconAsImage } from '@shared/lib/tileIconRender';

interface TileIconProps {
  imageUrl?: string | null;
  label: string;
  /** Lucide fallback when there is no module image. */
  fallbackIcon?: LucideIcon;
  className?: string;
}

/**
 * Module / quick-link glyph. Stroke-only module SVGs from the API are tinted
 * with primary via CSS mask; uploaded or CDN SVGs render as images so fills
 * and detail are preserved.
 */
export function TileIcon({ imageUrl, label, fallbackIcon: FallbackIcon, className }: TileIconProps): JSX.Element {
  const url = imageUrl?.trim();
  if (url) {
    if (shouldRenderTileIconAsImage(url)) {
      return (
        <img
          src={url}
          alt=""
          aria-hidden
          className={cn('h-8 w-8 shrink-0 object-contain opacity-85', className)}
        />
      );
    }

    return (
      <span
        aria-hidden
        className={cn('inline-block h-8 w-8 shrink-0 bg-primary-600 opacity-85', className)}
        style={{
          WebkitMaskImage: `url("${url}")`,
          maskImage: `url("${url}")`,
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      />
    );
  }

  if (FallbackIcon) {
    return <FallbackIcon className={cn('h-8 w-8 shrink-0 text-primary-600 opacity-85', className)} strokeWidth={1.75} />;
  }

  return (
    <span className={cn('font-display text-headline font-semibold leading-none text-primary-600', className)}>
      {label.charAt(0)}
    </span>
  );
}

/**
 * Shared home-grid tile chrome — full white tile, soft drop shadow, no
 * border (the shadow defines the edge cleanly enough on the paper bg).
 */
export const homeTileButtonClass =
  'flex h-full w-full min-w-0 flex-col items-center gap-2 rounded-xl bg-white px-1 py-3.5 text-center shadow-[0_4px_14px_rgba(0,0,0,0.10)] transition-transform duration-fast active:scale-[0.96]';

/** Shared home-grid tile label — soft, lower-weight caption text. */
export const homeTileLabelClass =
  'block w-full min-w-0 text-center text-caption font-normal leading-tight text-ink-secondary';

const TILE_LABEL_SCROLL_THRESHOLD_PX = 1;

export function TileLabel({
  label,
  className,
}: {
  label: string;
  className?: string;
}): JSX.Element {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflowShift, setOverflowShift] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    const updateOverflow = () => {
      const styles = window.getComputedStyle(container);
      const paddingLeft = Number.parseFloat(styles.paddingLeft || '0') || 0;
      const paddingRight = Number.parseFloat(styles.paddingRight || '0') || 0;
      const availableWidth = Math.max(0, container.clientWidth - paddingLeft - paddingRight);
      const nextShift = Math.max(0, Math.ceil(text.scrollWidth - availableWidth));
      setOverflowShift((currentShift) => (
        currentShift === nextShift ? currentShift : nextShift
      ));
    };

    updateOverflow();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflow);
      return () => window.removeEventListener('resize', updateOverflow);
    }

    const observer = new ResizeObserver(() => {
      updateOverflow();
    });

    observer.observe(container);
    observer.observe(text);
    window.addEventListener('resize', updateOverflow);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOverflow);
    };
  }, [label]);

  const isOverflowing = overflowShift > TILE_LABEL_SCROLL_THRESHOLD_PX;
  const marqueeStyle = isOverflowing
    ? ({
        '--tile-label-shift': `${overflowShift}px`,
        '--tile-label-duration': `${Math.max(4.2, Math.min(8.5, overflowShift / 16 + 3.4))}s`,
      } as CSSProperties)
    : undefined;

  return (
    <span
      ref={containerRef}
      className={cn(
        homeTileLabelClass,
        isOverflowing ? 'overflow-hidden text-left' : 'overflow-visible text-center',
        className,
      )}
    >
      <span
        ref={textRef}
        className={cn(
          'min-w-0 max-w-none whitespace-nowrap align-top',
          isOverflowing
            ? 'ip4x-tile-label-marquee block'
            : 'inline-block',
        )}
        style={marqueeStyle}
      >
        {label}
      </span>
    </span>
  );
}
